import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Popover, Badge } from 'antd-v5';
import { ROUTER_CART, ROUTER_CART_CONFIRMATION, ROUTER_CART_DETAILS, ROUTER_HOME } from 'app/AppRouter';
import { trackAppEvent } from 'app/AppTracker';
import { HelpIcon, ShopCartIcon, WeatherPartlyCloudyIcon } from 'assets/icons';
import { FirstHelpPopin } from 'components/Help/FirstHelpPopin';
import { getHeaderCountBadgeProps } from 'components/Page/Header/TopHeader/badgeStyles';
import { SupportMenu } from 'components/Page/Header/TopHeader/Support/SupportMenu';
import { SPopover } from 'components/Page/Header/TopHeader/TopHeader.styled';
import PrivateComponent from 'composers/PrivateComponent';
import { getDynatraceHealthDashboardLink } from 'domains/appContext/AppContext.store';
import { getBasketNumberOfItems } from 'domains/basket/Basket.store';
import { UserRole } from 'domains/user';
import CartPopover from 'pages/CartPage/CartPopover/CartPopover';
import FileReferencesUpload from 'pages/CartPage/CartStep/FileUpload';
import { Flex, Icon, Link, MarginBox, WithTooltip } from 'UI';
import { TRACKING_EVENT_GO_TO_CART_ICON } from 'utils/eventTracker/EventTracker.types';

interface IconMenuProps {
  iconColor: string;
  iconHoverFill?: string;
  showExtendedIcons?: boolean;
}

const IconMenu = ({ iconColor, iconHoverFill, showExtendedIcons = true }: IconMenuProps) => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const numberOfItems = useSelector(getBasketNumberOfItems);
  const isCartPage = [ROUTER_CART, ROUTER_CART_DETAILS, ROUTER_CART_CONFIRMATION].includes(location.pathname);
  const dynatraceHealthDashboardUrl = useSelector(getDynatraceHealthDashboardLink);

  const [supportVisible, setSupportVisible] = useState<boolean>(false);
  const [canShowBasketPreview, setCanShowBasketPreview] = useState<boolean>(false);

  const setDefaultCartState = () => {
    history.push(ROUTER_CART);
  };

  useEffect(() => {
    if (isCartPage) {
      setCanShowBasketPreview(false);
    }
  }, [isCartPage]);

  const handleBasketPreviewOpenChange = (newOpen: boolean) => {
    if (!isCartPage) {
      setCanShowBasketPreview(newOpen);
    }
  };

  return (
    <Flex align={'center'} gap={5}>
      {showExtendedIcons && (
        <PrivateComponent
          render={() => (
            <MarginBox my={'auto'} mx={5}>
              <WithTooltip title={t('header.application_health', 'Application health')} placement={'bottom'}>
                <Icon
                  IconComponent={WeatherPartlyCloudyIcon}
                  onClick={() => {
                    window.open(dynatraceHealthDashboardUrl, '_blank', 'noopener,noreferrer');
                  }}
                  size={28}
                  display={'block'}
                  color={iconColor}
                  hoverFill={iconHoverFill}
                  data-cy={'top-link-application-check'}
                />
              </WithTooltip>
            </MarginBox>
          )}
          requiredRights={[UserRole.COMMAND, UserRole.CONNECT_COMMANDE]}
        />
      )}
      <FileReferencesUpload />
      {showExtendedIcons && (
        <PrivateComponent
          render={() => (
            <MarginBox my={'auto'} mx={5}>
              <FirstHelpPopin streamId={'close'} popinId={'close'} placement={'bottomLeft'} hideCounter>
                <SPopover
                  id={'top_header-support-popover'}
                  content={<SupportMenu setSupportVisible={setSupportVisible} />}
                  trigger={'click'}
                  placement={'bottomRight'}
                  visible={supportVisible}
                  onVisibleChange={(visible) => setSupportVisible(visible)}
                >
                  <WithTooltip title={t('common.action.help', 'Help')} placement={'bottom'}>
                    <Icon
                      IconComponent={HelpIcon}
                      size={28}
                      display={'block'}
                      color={iconColor}
                      hoverFill={iconHoverFill}
                      data-cy={'top-link-help'}
                    />
                  </WithTooltip>
                </SPopover>
              </FirstHelpPopin>
            </MarginBox>
          )}
          requiredRights={[UserRole.COMMAND, UserRole.CONNECT_COMMANDE]}
        />
      )}
      <PrivateComponent
        render={() => (
          <MarginBox my={'auto'} mx={5}>
            <FirstHelpPopin streamId={ROUTER_HOME} popinId={`${ROUTER_HOME}_basket`} placement={'bottomLeft'}>
              <Popover
                content={<CartPopover />}
                arrow={false}
                open={canShowBasketPreview && !!numberOfItems}
                onOpenChange={handleBasketPreviewOpenChange}
              >
                <div>
                  <Link to={ROUTER_CART} data-cy={'top-link-cart'}>
                    <Flex>
                      <Badge {...getHeaderCountBadgeProps(numberOfItems)}>
                        <WithTooltip
                          title={t('cart', 'Cart')}
                          placement={'bottom'}
                          open={canShowBasketPreview && !!numberOfItems ? false : undefined}
                        >
                          <Icon
                            IconComponent={ShopCartIcon}
                            onClick={() => {
                              trackAppEvent(TRACKING_EVENT_GO_TO_CART_ICON);
                              setDefaultCartState();
                            }}
                            size={28}
                            display={'block'}
                            color={iconColor}
                            hoverFill={iconHoverFill}
                          />
                        </WithTooltip>
                      </Badge>
                    </Flex>
                  </Link>
                </div>
              </Popover>
            </FirstHelpPopin>
          </MarginBox>
        )}
        requiredRights={[UserRole.COMMAND, UserRole.CONNECT_COMMANDE]}
      />
    </Flex>
  );
};

export default IconMenu;
